import { forwardRef, useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, Variants } from 'framer-motion'
import { PiCaretDownBold } from 'react-icons/pi'

interface SelectOption {
  value: string
  label: string
}

interface SelectProps {
  label?: string
  error?: string
  className?: string
  options: SelectOption[]
  value?: string
  defaultValue?: string
  onChange?: (value: string) => void
  name?: string
}

const useClickOutside = (handler: () => void) => {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        handler()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [handler])

  return ref
}

const useFilteredOptions = (options: SelectOption[], searchQuery: string) => {
  const [filteredOptions, setFilteredOptions] = useState(options)

  useEffect(() => {
    if (searchQuery) {
      const normalized = searchQuery.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      const filtered = options.filter(option => {
        const normalizedLabel = option.label.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        return normalizedLabel.includes(normalized)
      })
      setFilteredOptions(filtered)
    } else {
      setFilteredOptions(options)
    }
  }, [searchQuery, options])

  return filteredOptions
}

const dropdownVariants: Variants = {
  open: {
    opacity: 1,
    y: 0,
    display: "block",
    transition: {
      duration: 0.2,
      display: { delay: 0 }
    }
  },
  closed: {
    opacity: 0,
    y: -10,
    transition: {
      duration: 0.2,
      display: { delay: 0.2 }
    },
    transitionEnd: {
      display: "none"
    }
  }
}

const SelectInput = ({ 
  isOpen, 
  searchQuery, 
  selectedOption, 
  onSearchChange,
  placeholder
}: { 
  isOpen: boolean
  searchQuery: string
  selectedOption?: SelectOption
  onSearchChange: (value: string) => void
  placeholder: string
}) => (
  <div className="flex items-center justify-between">
    {isOpen ? (
      <input
        type="text"
        value={searchQuery}
        onChange={(e) => onSearchChange(e.target.value)}
        onClick={(e) => e.stopPropagation()}
        placeholder={placeholder}
        className="w-full outline-hidden bg-transparent"
        autoFocus
      />
    ) : (
      <span className={`${selectedOption ? '' : 'text-zinc-900'}`}>
        {selectedOption ? selectedOption.label : ''}
      </span>
    )}
    <motion.div
      animate={{ rotate: isOpen ? 180 : 0 }}
      transition={{ duration: 0.2 }}
    >
      <PiCaretDownBold className="w-5 h-5 text-zinc-400" />
    </motion.div>
  </div>
)

const DropdownList = ({ 
  options, 
  selectedValue, 
  onSelect 
}: { 
  options: SelectOption[]
  selectedValue: string
  onSelect: (value: string) => void 
}) => (
  <div className="max-h-[14.5rem] overflow-y-auto p-1">
    {options.map((option) => (
      <div
        key={option.value}
        onClick={() => onSelect(option.value)}
        className={`
          p-4
          text-base
          rounded-xl
          text-zinc-700
          ${option.value === selectedValue ? 'bg-gray-100' : ''}
        `}
      >
        {option.label}
      </div>
    ))}
  </div>
)

export const Select = forwardRef<HTMLInputElement, SelectProps>(
  ({
    label,
    error,
    options,
    value: controlledValue,
    defaultValue,
    onChange,
    name,
    className = '',
  }, ref) => {
    const { t } = useTranslation()
    const [isOpen, setIsOpen] = useState(false)
    const [internalValue, setInternalValue] = useState(defaultValue || '')
    const [searchQuery, setSearchQuery] = useState('')
    
    const value = controlledValue !== undefined ? controlledValue : internalValue
    const selectedOption = options.find(option => option.value === value)
    const filteredOptions = useFilteredOptions(options, searchQuery)

    const handleClose = () => {
      setIsOpen(false)
      setSearchQuery('')
    }

    const selectRef = useClickOutside(handleClose)

    const handleSelect = (optionValue: string) => {
      setInternalValue(optionValue)
      onChange?.(optionValue)
      handleClose()
    }

    useEffect(() => {
      if (defaultValue) {
        setInternalValue(defaultValue)
      }
    }, [defaultValue])

    return (
      <div className="relative flex flex-col">
        {label && (
          <label className="text-sm text-zinc-700 mb-2">
            {label}
          </label>
        )}
        <div className="relative" ref={selectRef}>
          <div
            onClick={() => setIsOpen(!isOpen)}
            className={`
              w-full
              px-4 
              py-3 
              rounded-xl
              outline-hidden
              text-base
              text-zinc-700
              transition-all
              duration-200
              ease-in-out
              cursor-pointer
              border
              ${isOpen ? 'border-blue-600 ring-4 ring-blue-200' : ''}
              ${error ? 'border-red-500' : ''}
              ${className}
            `}
          >
            <SelectInput
              isOpen={isOpen}
              searchQuery={searchQuery}
              selectedOption={selectedOption}
              onSearchChange={setSearchQuery}
              placeholder={t('common.searchPlaceholder')}
            />
          </div>

          <input
            ref={ref}
            type="hidden"
            name={name}
            value={value}
          />

          <motion.div
            initial="closed"
            animate={isOpen && filteredOptions.length > 0 ? "open" : "closed"}
            variants={dropdownVariants}
            className="absolute z-50 w-full mt-2 bg-white border border-gray-200 rounded-2xl shadow-lg"
          >
            <DropdownList
              options={filteredOptions}
              selectedValue={value}
              onSelect={handleSelect}
            />
          </motion.div>
        </div>
        {error && (
          <motion.span
            key={error}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-sm text-red-500 mt-3">
            {error}
          </motion.span>
        )}
      </div>
    )
  }
)

Select.displayName = 'Select'

export default Select